// Whether THIS disruption, at THIS moment, is worth a buzz.
//
// PURE, AND WITH ONE IMPORT, `./modes.js`, which imports nothing. Not Vue, not the store, not the browser,
// not even `src/network.js`. The constraint is the same one `src/severity.js`
// is under and it is not tidiness: this file is imported by BOTH the phone and
// `server/push.mjs` on Cloud Run, and the two MUST reach the same verdict about
// the same notice. A second copy of these rules living server-side would drift
// within a month, and the symptom would be somebody's 6:40am going quiet while
// the app on their phone insisted it had told them.
//
// It also forces the file split. `server/push.mjs` cannot import
// `src/network.js` — that pulls in a JSON import Node refuses without an
// attribute, and `src/departures.js` behind it — so the server never resolves
// anything. A rule arrives here with its path ALREADY RESOLVED, as plain data:
//
//   { id, from, to, fromName, toName,
//     start: '08:00', end: '09:30', lead: 30, days: [1, 2, 3, 4, 5],
//     active: true, mutedUntil: null,
//     path: { stops: [1034, 1155, …], routes: [11, 14] }, resolvedAt }
//
// `days` is in JS `getDay()` order — 0 is Sunday — because that is what both
// callers have in their hands and converting at two call sites is one more
// place to be off by one. Resolving the path is `src/rule-path.js`, which is
// client-only for exactly the reason above.
//
// WHAT THIS REPLACED, and why. Alerts were filtered by line and silenced by one
// overnight quiet window. That is too blunt to be useful: a commuter does not
// care about the Pakenham line, they care about Carnegie to State Library
// between 08:00 and 09:30 on weekdays, and about getting home between 16:30 and
// 18:00. Everything below exists to express that trip and nothing wider.

// ── Local time, without a timezone dependency ────────────────────────────────

/**
 * The device's local day and minute-of-day for an instant.
 *
 * The trick is `server/push.mjs`'s and it is copied rather than imported, since
 * this file imports nothing: shift the instant by the offset, then read the UTC
 * fields, and they now carry the device's local wall clock. The sweep runs in
 * Cloud Run's UTC, and a Melbourne 08:00–09:30 window evaluated in UTC is
 * 18:00–19:30 AEST — the wrong half of the day, every day.
 *
 * `tzOffsetMinutes` is minutes EAST of UTC, so Melbourne is +600. Note that
 * this is the opposite sign to `Date.prototype.getTimezoneOffset()`, which the
 * caller storing the value has to flip.
 */
function localAt(at, tzOffsetMinutes = 0) {
  const ms = at instanceof Date ? at.getTime() : Number(at);
  if (!Number.isFinite(ms)) return null;
  const offset = Number(tzOffsetMinutes);
  const local = new Date(ms + (Number.isFinite(offset) ? offset : 0) * 60_000);
  return {
    ms,
    day: local.getUTCDay(),
    minutes: local.getUTCHours() * 60 + local.getUTCMinutes(),
  };
}

/** 'HH:MM' as minutes past local midnight, or null if it is not that. */
function toMinutes(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

const DAY_MINUTES = 1440;
const WEEK_MINUTES = 7 * DAY_MINUTES;

/** The day numbers on a rule or window, as a clean array of 0..6. */
function daysOf(row) {
  return (Array.isArray(row?.days) ? row.days : [])
    .map(Number)
    .filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
}

// ── Quiet windows ────────────────────────────────────────────────────────────

/**
 * Is `at` inside any of these quiet windows?
 *
 * A window is `{ id, from: '22:00', to: '06:00', days: [0,1,2,3,4,5,6] }`.
 *
 * WRAPPING MIDNIGHT IS WRITTEN FIRST because it is the normal case — 22:00 to
 * 06:00 is what almost everybody sets — which is the same reasoning, and the
 * same shape of test, as `isQuiet` in `src/app-state.js` and `inQuietHours` in
 * `server/push.mjs`. The end is EXCLUSIVE, so a 22:00–06:00 window is over at
 * 06:00 sharp and the 6:40am this app exists for is never inside it.
 *
 * The day test asks about the day the window STARTED, not the day the clock
 * shows. A Friday-night 22:00–06:00 window is still Friday's window at 02:00 on
 * Saturday; testing the Saturday would cut it in half at midnight and let an
 * alert through at 2am to somebody who asked for silence.
 */
export function inAnyQuietWindow(windows, at = Date.now(), tzOffsetMinutes = 0) {
  const local = localAt(at, tzOffsetMinutes);
  if (!local) return false;
  return (Array.isArray(windows) ? windows : []).some((w) => coversMinute(w, local));
}

function coversMinute(window, local) {
  const from = toMinutes(window?.from);
  const to = toMinutes(window?.to);
  // A malformed window silences nothing. Holding every alert because somebody
  // typed "9pm" is the worse of the two failures.
  if (from === null || to === null) return false;

  const days = daysOf(window);
  const onDay = (d) => days.length === 0 || days.includes(d);
  const yesterday = (local.day + 6) % 7;

  if (from > to) {
    // Wraps midnight. Tonight's half belongs to today, this morning's half
    // belongs to yesterday — which is the day whose `days` entry must be set.
    if (local.minutes >= from) return onDay(local.day);
    if (local.minutes < to) return onDay(yesterday);
    return false;
  }

  return local.minutes >= from && local.minutes < to && onDay(local.day);
}

// ── Is a rule live right now? ────────────────────────────────────────────────

/**
 * Is this rule watching, at this instant?
 *
 * Three questions, and all three have to answer yes: the rule is switched on,
 * it is not muted, and the clock is inside `[start − lead, end]` on a day the
 * rule covers. It knows NOTHING about quiet windows — that decision is made
 * once, in `shouldNotify`, and threading it through here is how it ends up
 * being made twice and differently.
 *
 * THE LEAD CAN REACH INTO THE PREVIOUS DAY. A 00:15 window with a 30-minute
 * lead starts at 23:45 the night before, and the day test then applies to the
 * WINDOW's day, not to the lead's: a Monday rule is live at 23:45 on Sunday.
 * Getting this backwards is silent — it just means the first alert of a very
 * early shift never arrives.
 *
 * The end is INCLUSIVE. Somebody who said 09:30 meant that 09:30 is still their
 * commute, and the cost of the extra minute is one notification a year.
 *
 * A window whose end is not after its start (09:30 to 08:00) never matches.
 * That is a data fault rather than an overnight commute — `parseRule` refuses
 * to produce one and the editor should too — and guessing which way round it
 * was meant is worse than doing nothing.
 */
export function activeAt(rule, at = Date.now(), tzOffsetMinutes = 0) {
  return activeWindowEnd(rule, at, tzOffsetMinutes) !== null;
}

/**
 * WHERE the window this rule is currently inside ends, or null if it is not
 * inside one. The whole of `activeAt` is this function asking "is there one".
 *
 * The answer is minutes counted from LOCAL MIDNIGHT TODAY, which is why it can
 * exceed 1440: a 00:15 window reached at 23:45 through its lead ends at 00:15
 * TOMORROW, and returning 15 would hand the caller a moment in the past.
 *
 * TWO CALLERS NEED THE NUMBER and both need it to be THIS one. `muteEndFor`
 * uses it because it is what "mute this window" means, and `activeTrip` in
 * `src/commute.js` uses it to choose between two rules that are both live —
 * the one ending soonest wins. A second reading of the same fields in either
 * place is how a mute comes to end at a moment the rule does not agree is its
 * end, or a widget swaps trips at a minute the alerts do not.
 */
export function activeWindowEnd(rule, at = Date.now(), tzOffsetMinutes = 0) {
  if (!rule || rule.active === false) return null;

  const local = localAt(at, tzOffsetMinutes);
  if (!local) return null;

  // Muted until an instant in the future. An expired `mutedUntil` is simply
  // over — it is left on the row rather than cleaned up, because the cleanup
  // would be a write on every read.
  const muted = Date.parse(rule.mutedUntil ?? '');
  if (Number.isFinite(muted) && muted > local.ms) return null;

  const start = toMinutes(rule.start);
  const end = toMinutes(rule.end);
  if (start === null || end === null) return null;

  const lead = Number.isFinite(Number(rule.lead)) ? Math.max(0, Number(rule.lead)) : 0;
  const days = daysOf(rule);
  const onDay = (d) => days.length === 0 || days.includes(d);
  const from = start - lead;

  // Today's window, clamped at midnight: anything before it belonged to
  // yesterday evening and is handled by the branch below.
  if (local.minutes >= Math.max(0, from) && local.minutes <= end && onDay(local.day)) return end;

  // The lead spilling backwards past midnight, which is TOMORROW's window.
  if (from < 0 && local.minutes >= DAY_MINUTES + from && onDay((local.day + 1) % 7)) {
    return DAY_MINUTES + end;
  }

  return null;
}

import { isAdapterLine } from './modes.js';

// ── Does a disruption concern this trip? ─────────────────────────────────────

/** Stop ids off a disruption's `stops`, which PTV gives as `[{ stopId, name }]`. */
function namedStopIds(disruption) {
  return (Array.isArray(disruption?.stops) ? disruption.stops : [])
    .map((s) => Number(typeof s === 'object' && s !== null ? s.stopId : s))
    .filter(Number.isFinite);
}

function intersects(ids, set) {
  return ids.some((id) => set.has(id));
}

// ── Tram notices at the level of a stop ──────────────────────────────────────

/** Stop ids as plain numbers, whatever they arrive as. */
const numbers = (list) => (Array.isArray(list) ? list : []).map(Number).filter(Number.isFinite);

/**
 * These stop ids and every stop BETWEEN them along a route.
 *
 * `order` is the route's directions, each the ids of its stops in running order
 * (`[[3200, 3201, …], […]]`). Within one direction the first and last of the
 * given ids bound a stretch, and the stretch is included: that is "the segment
 * between" two stops somebody has starred, and "the stretch" a diversion
 * covers when the notice names only its two ends.
 */
export function spanOf(ids, order) {
  const want = new Set(numbers(ids));
  const out = new Set(want);
  for (const dir of Array.isArray(order) ? order : []) {
    const list = numbers(dir);
    let first = -1;
    let last = -1;
    list.forEach((id, i) => {
      if (!want.has(id)) return;
      if (first < 0) first = i;
      last = i;
    });
    if (first >= 0) for (const id of list.slice(first, last + 1)) out.add(id);
  }
  return [...out];
}

/**
 * Is this notice about somewhere the person never goes?
 *
 * A TRAM NOTICE IS ROUTE-WIDE UNLESS IT IS PROVABLY ELSEWHERE. All of these
 * must hold for a "yes":
 *
 *   * the notice is stop-scoped: PTV named stops on it (`stopScoped`);
 *   * the person has stops on this route — `mine`, the tram stops they have
 *     starred that this route calls at — so there is somewhere to measure from;
 *   * the notice names at least one stop, whether PTV's (`stops`) or the
 *     caller's reading of the prose (`affected`);
 *   * and the stretch the notice covers shares no stop with the stretch they
 *     use: their stops and the segment between them (`spanOf`).
 *
 * Anything else — a route-wide notice, no stars on the route, a notice naming
 * nothing we can place — is `false`, which is today's behaviour: the route is
 * watched, so it buzzes. A missed alert is the failure this app exists to
 * prevent; an alert about the far end of a route is only a quiet row.
 */
export function outOfReach(disruption, { mine, order, affected } = {}) {
  if (!disruption?.stopScoped) return false;
  const watching = numbers(mine);
  if (!watching.length) return false;
  const named = [...namedStopIds(disruption), ...numbers(affected)];
  if (!named.length) return false;
  const reach = new Set(spanOf(watching, order));
  return !spanOf(named, order).some((id) => reach.has(id));
}

/**
 * Does this disruption concern the rule's path?
 *
 * THREE TIERS, MOST EXACT FIRST, and each one is tried in turn — a tier that
 * does not match does NOT veto the tiers below it.
 *
 *   1. The stop ids PTV itself names on the notice. This is the only evidence
 *      that comes from PTV rather than from us, so it goes first.
 *   2. Otherwise `affected` — stop ids the CALLER derived from the prose, which
 *      on the phone is `affectedStops` from `src/network.js` reading "buses
 *      replace trains between Caulfield and Dandenong". The server passes
 *      nothing, because it cannot import that file; it simply gets tier 3.
 *   3. Otherwise the notice's line being one of the lines the trip rides.
 *
 * TIER 3 OVER-NOTIFIES, DELIBERATELY. A delay named at Pakenham reaches a
 * Carnegie-to-city rule because both are route 11, and a notice that names a
 * station nowhere near the path still matches on its line. That is the
 * behaviour we want: a missed alert is the failure this app exists to prevent,
 * and a commuter who gets one alert too many deletes a notification, while a
 * commuter who gets one too few misses the train and stops trusting the app.
 * The tiers above exist to let a notice match when its LINE does not — a
 * closure at an interchange the trip changes at — not to suppress one.
 */
export function touches(rule, disruption, opts = {}) {
  const { affected } = opts;
  // TIER 0, A LINE OF AN ADAPTER MODE (Melbourne's trams): every rule's trip. A
  // rule is planned between two railway stations, so its path can never name
  // such a route — and one somebody watches is, as often as not, the first or last leg of the
  // very commute the rule describes. So a tram notice is matched at the level
  // of the ROUTE and the rule decides WHEN: it buzzes inside a rule's window
  // and is held, like any other, until one opens. The person is watching the
  // route (the sweep and the phone only ever ask this about watched lines),
  // so the route is relevant; the rule is still the clock.
  //
  // The bands are the mode registry's (`src/modes.js`).
  //
  // NARROWED BY STOP when the caller says which stops the person uses on the
  // route (`mine`) and the notice is stop-scoped — see `outOfReach`.
  if (isAdapterLine(disruption?.routeId)) return !outOfReach(disruption, opts);

  const stops = new Set((rule?.path?.stops ?? []).map(Number).filter(Number.isFinite));
  const routes = new Set((rule?.path?.routes ?? []).map(Number).filter(Number.isFinite));

  // Tier 1: PTV's own stop list on the notice.
  if (stops.size && intersects(namedStopIds(disruption), stops)) return true;

  // Tier 2: what the caller read out of the prose.
  if (stops.size && Array.isArray(affected)
    && intersects(affected.map(Number).filter(Number.isFinite), stops)) return true;

  // Tier 3: the line. Wide on purpose — see above.
  const routeId = Number(disruption?.routeId);
  return Number.isFinite(routeId) && routes.has(routeId);
}

// ── The one decision both callers make ───────────────────────────────────────

/**
 * Notify, or not, and why.
 *
 * `{ notify, reason, ruleId? }` — small enough to log on every sweep, and the
 * reason is there so that "why did my phone not buzz" is answerable from a log
 * line instead of from a re-run.
 *
 * The truth table, which is the product decision and not this file's to change:
 *
 *   inside any quiet window  → never, whatever the rules say      'quiet'
 *   no rules configured      → notify, which is today's behaviour 'no-rules'
 *   a rule activeAt + touches→ notify                             'rule'
 *   otherwise                → do not notify                      'no-rule'
 *
 * QUIET IS ABSOLUTE and it is tested in ONE place — the line below. There is no
 * per-rule override and no second quiet test hidden inside `activeAt` or
 * `touches`, which is what keeps this table readable and what would make a
 * change of mind about precedence a change to one line here.
 *
 * Note the last row: once somebody has written rules, the rules are the whole
 * filter. A disruption that touches no rule is not theirs, even at 08:15 on a
 * Tuesday. That is the point of having described the trip.
 */
export function shouldNotify({ rules, quiet } = {}, disruption, at = Date.now(), tzOffsetMinutes = 0, opts = {}) {
  // One quiet test, one place, and it wins outright.
  if (inAnyQuietWindow(quiet, at, tzOffsetMinutes)) return { notify: false, reason: 'quiet' };

  // A tram notice at the far end of a route, away from every stop the person
  // uses on it, is a row on the Alerts tab and not a buzz. Asked before the
  // rules, so it holds with no rules too. NOT held for later: it will not
  // become theirs by waiting.
  if (outOfReach(disruption, opts)) return { notify: false, reason: 'out-of-reach' };

  const list = (Array.isArray(rules) ? rules : []).filter(Boolean);

  // Nobody has described a trip yet, so nothing is being asked for and the old
  // behaviour stands: the quiet windows alone decide, and they already have.
  if (!list.length) return { notify: true, reason: 'no-rules' };

  const hit = list.find((rule) => activeAt(rule, at, tzOffsetMinutes) && touches(rule, disruption, opts));
  if (hit) return { notify: true, reason: 'rule', ruleId: hit.id ?? null };

  return { notify: false, reason: 'no-rule' };
}

// ── Warning about a rule that will never fire ────────────────────────────────

/** A rule's watching window as [start − lead, end] in minutes, or null. */
function ruleSpan(rule) {
  const start = toMinutes(rule?.start);
  const end = toMinutes(rule?.end);
  if (start === null || end === null || end <= start) return null;
  const lead = Number.isFinite(Number(rule?.lead)) ? Math.max(0, Number(rule.lead)) : 0;
  return { from: start - lead, to: end };
}

/** Absolute minute-of-week spans for a row repeated on each of its days. */
function weekSpans(days, from, to) {
  const on = days.length ? days : [0, 1, 2, 3, 4, 5, 6];
  return on.map((d) => ({ from: d * DAY_MINUTES + from, to: d * DAY_MINUTES + to }));
}

/** Do two minute-of-week spans overlap, given the week is a circle? */
function spansOverlap(a, b) {
  // Both are half-open [from, to). Touching end to start is NOT an overlap:
  // a quiet window ending at 08:00 and a rule starting at 08:00 do not collide,
  // and warning about that would train people to ignore the warning.
  for (const x of unwrap(a)) {
    for (const y of unwrap(b)) {
      if (x.from < y.to && y.from < x.to) return true;
    }
  }
  return false;
}

/** A span normalised into one or two pieces inside [0, WEEK_MINUTES). */
function unwrap(span) {
  let { from, to } = span;
  const length = Math.min(to - from, WEEK_MINUTES);
  from = ((from % WEEK_MINUTES) + WEEK_MINUTES) % WEEK_MINUTES;
  to = from + length;
  if (to <= WEEK_MINUTES) return [{ from, to }];
  return [{ from, to: WEEK_MINUTES }, { from: 0, to: to - WEEK_MINUTES }];
}

/**
 * Which quiet windows swallow part of this rule.
 *
 * Quiet is absolute, so a rule sitting inside one is a rule that will never
 * fire — and the person who wrote it has no way of knowing that from either
 * screen. The editor asks this at save time and SAYS so ("this sits inside your
 * 09:00–16:00 quiet window — you will not be alerted then"), which is the
 * honest version of the per-rule override switch this replaced: a switch hides
 * the conflict behind a default, a sentence shows it.
 *
 * Returns the offending windows themselves, in the order they were given, so
 * the caller can name them. An empty array is the good case.
 *
 * Compared across the whole WEEK, not one day: a rule's lead and a quiet
 * window's wrap both cross midnight, in opposite directions, and a 23:45 lead
 * into a Monday window collides with Sunday night's quiet.
 */
export function overlapsQuiet(rule, windows) {
  const span = ruleSpan(rule);
  if (!span) return [];

  const ruleSpans = weekSpans(daysOf(rule), span.from, span.to);

  return (Array.isArray(windows) ? windows : []).filter((w) => {
    const from = toMinutes(w?.from);
    const to = toMinutes(w?.to);
    if (from === null || to === null) return false;
    // A wrapping window runs past midnight into the following day; its day
    // numbers name the day it STARTS, exactly as `inAnyQuietWindow` reads them.
    const quietSpans = weekSpans(daysOf(w), from, from < to ? to : to + DAY_MINUTES);
    return quietSpans.some((q) => ruleSpans.some((r) => spansOverlap(r, q)));
  });
}

// ── Muting ───────────────────────────────────────────────────────────────────
//
// Muting is not switching off. "Not this morning" is a different sentence from
// "never again", and a commuter who is working from home today needs the first
// one — which means `active` stays true and the row keeps its place in the list
// rather than greying out and being forgotten about until Thursday.
//
// All four are pure and return a NEW rule. Nothing here mutates: these are
// called from a screen holding a reactive row, and mutating it in place is how
// a list stops re-rendering.

/** Muted until an instant. Anything unparseable clears the mute rather than inventing one. */
export function muteUntil(rule, iso) {
  const ms = iso instanceof Date ? iso.getTime() : Date.parse(iso ?? '');
  return { ...rule, mutedUntil: Number.isFinite(ms) ? new Date(ms).toISOString() : null };
}

/**
 * Muted until the next LOCAL midnight.
 *
 * Local, via the same shift-then-read-UTC trick as everything else here, so
 * "for today" ends when the person's day ends and not when Cloud Run's does —
 * a Melbourne evening is already tomorrow in UTC, and a mute that expired the
 * moment it was set would be the most confusing possible bug to report.
 */
export function muteForToday(rule, at = Date.now(), tzOffsetMinutes = 0) {
  const local = localAt(at, tzOffsetMinutes);
  if (!local) return { ...rule };
  return muteUntil(rule, new Date(local.ms + (DAY_MINUTES - local.minutes) * 60_000));
}

/** Muted for an hour. No timezone involved — an hour is an hour anywhere. */
export function muteForAnHour(rule, at = Date.now()) {
  const ms = at instanceof Date ? at.getTime() : Number(at);
  if (!Number.isFinite(ms)) return { ...rule };
  return muteUntil(rule, new Date(ms + 60 * 60_000));
}

/** Watching again, now. */
export function unmute(rule) {
  return { ...rule, mutedUntil: null };
}

// ── Muting the whole DEVICE, from a notification's quick actions ─────────────
//
// The four above mute one RULE, from a screen. This one mutes everything
// reaching this phone, from the notification itself — the two buttons under a
// disruption alert, `mute-window` and `mute-day`, which are the only controls
// in this app somebody uses without unlocking anything.
//
// IT IS HERE, IN THE IMPORT-FREE FILE, FOR THE USUAL REASON. The server
// answers the button when the phone has signal (`POST /api/push/mute`), and
// the phone answers it alone when the alert was a local one. Two
// implementations of "the end of the window I am in" would drift, and the
// symptom would be a mute that the app says ends at 09:30 and the server says
// ends at 10:30 — invisible until somebody is woken by the difference.

/** With no window to hide inside, an hour. Long enough to be worth pressing. */
const LOOSE_MUTE_MINUTES = 60;

/** The instant of local midnight at the start of `local`'s day. */
function startOfLocalDay(local, tzOffsetMinutes) {
  const offset = Number.isFinite(Number(tzOffsetMinutes)) ? Number(tzOffsetMinutes) : 0;
  const shifted = new Date(local.ms + offset * 60_000);
  const midnight = Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth(), shifted.getUTCDate());
  return midnight - offset * 60_000;
}

/**
 * When a mute taken from a notification ends, as an ISO instant.
 *
 *   'day'     the next LOCAL midnight. "Not today" ends when the person's day
 *             ends, not when Cloud Run's does — a Melbourne evening is already
 *             tomorrow in UTC, and a mute that expired the moment it was set
 *             would be the most confusing possible bug to report.
 *   'window'  the end of the rule window being watched right now, lead
 *             included, because "this window" is the commute the alert
 *             interrupted. If two rules overlap, the LATEST of their ends: the
 *             person asked for quiet, and ending at the earlier one would buzz
 *             them again inside a trip they are still on.
 *   neither   an hour. Somebody who has described no trip, or who pressed it
 *             outside every window they did describe, still meant "not now",
 *             and the honest reading of "not now" with nothing to measure it
 *             against is a round hour.
 *
 * `tz` is minutes EAST of UTC, the same sign every other function here takes.
 * Unparseable input returns null rather than inventing a mute — a caller with
 * no answer must leave the device unmuted, never silence it indefinitely.
 */
export function muteEndFor({ until, rules, now = Date.now(), tz = 0 } = {}) {
  const local = localAt(now, tz);
  if (!local) return null;
  if (until !== 'window' && until !== 'day') return null;

  const midnight = startOfLocalDay(local, tz);

  if (until === 'day') return new Date(midnight + DAY_MINUTES * 60_000).toISOString();

  let latest = null;
  for (const rule of Array.isArray(rules) ? rules : []) {
    const end = activeWindowEnd(rule, now, tz);
    if (end === null) continue;
    const at = midnight + end * 60_000;
    if (latest === null || at > latest) latest = at;
  }

  // A window whose end has already passed to the minute (pressed at 09:30 on an
  // 09:30 end) would be a mute of zero length, which reads as a button that did
  // nothing. The loose hour is the better answer.
  if (latest !== null && latest > local.ms) return new Date(latest).toISOString();
  return new Date(local.ms + LOOSE_MUTE_MINUTES * 60_000).toISOString();
}

// ── The one-line human form ──────────────────────────────────────────────────

const DAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKENDS = [0, 6];

const sameDays = (a, b) => a.length === b.length && b.every((d) => a.includes(d));

/**
 * The row's subtitle: `WEEKDAYS 08:00–09:30`, `EVERY DAY 16:30–18:00`,
 * `MON WED FRI 07:00–08:00`.
 *
 * The three common sets get a word because a word is read at a glance and
 * `MON TUE WED THU FRI` is read letter by letter. Anything else is spelled out
 * in week order — Sunday first, matching the stored `getDay()` numbers, so that
 * what is shown and what is stored cannot disagree.
 *
 * The lead is not shown. It is a detail of when the watching starts, not of
 * what the trip IS, and putting "(from 07:30)" on every row costs more
 * attention than it returns.
 */
export function describeRule(rule) {
  const days = [...new Set(daysOf(rule))].sort((a, b) => a - b);

  let label;
  if (days.length === 7) label = 'EVERY DAY';
  else if (sameDays(days, WEEKDAYS)) label = 'WEEKDAYS';
  else if (sameDays(days, WEEKENDS)) label = 'WEEKENDS';
  else label = days.map((d) => DAY_LABELS[d]).join(' ');

  // An en dash, not a hyphen: the row is a range, and at this size a hyphen
  // reads as part of the digits beside it.
  const span = `${rule?.start ?? ''}–${rule?.end ?? ''}`;
  return label ? `${label} ${span}` : span;
}
